import {
  Building2,
  Wallet,
  Layers,
  Users,
  MapPin,
  Clock,
  CalendarDays,
  Shield,
  FileText,
  HeartHandshake,
  Receipt,
  UserCog,
  Cog,
  CreditCard,
  Upload,
  Fingerprint,
  PenLine,
  CheckCircle2,
  CalendarRange,
  CalendarOff,
  Gift,
  Briefcase,
  UserCheck,
  Lock,
  ClipboardList,
  LayoutGrid,
  GitBranch,
  Network,
  FunctionSquare, // Formula Builder
  Zap,
  BookOpen,
  PlayCircle,
  History,
  Scale,
  BarChart3,
  Fuel,
  IndianRupee,
  WalletCards,
} from "lucide-react";
import { PAYROLL_ADVANCED_TABS } from "../Moduels/Salary/v2/payrollSetupNav";

const PAYROLL_TOOL_META = {
  "/salary-v2/formula-builder": { icon: FunctionSquare, description: "Build and test salary formulas" },
  "/salary-v2/overtime/formula": { icon: FunctionSquare, description: "Build and test overtime pay formulas" },
  "/salary-v2/derived-variables": { icon: Layers, description: "Named variables such as MONTHLY_CTC and Gross" },
  "/salary-v2/user-guide": { icon: BookOpen, description: "Salary module documentation and examples" },
  "/salary-v2/benefits-deductions": { icon: HeartHandshake, description: "Benefits and recurring deductions" },
  "/salary-v2/groups": { icon: Network, description: "Group components for templates and reporting" },
  "/salary-v2/scheduled": { icon: CalendarRange, description: "Scheduled earnings and deductions" },
  "/salary-v2/arrears": { icon: History, description: "Arrear runs and back-pay adjustments" },
  "/salary-v2/statutory": { icon: Scale, description: "Salary-side statutory configuration" },
  "/salary-v2/simulator": { icon: PlayCircle, description: "Preview payslip breakdown for a sample CTC" },
  "/salary-v2/dependencies": { icon: GitBranch, description: "Component dependency graph and cycles" },
  "/salary-v2/audit": { icon: ClipboardList, description: "Salary configuration change history" },
  "/tools/ctc-planner": { icon: BarChart3, description: "Plan CTC splits and compare structures" },
  "/settings/statutory": { icon: Shield, description: "Compliance and statutory console" },
};

const PAYROLL_ADVANCED_SETTINGS_ITEMS = PAYROLL_ADVANCED_TABS.map((tab, index) => {
  const meta = PAYROLL_TOOL_META[tab.to] || {};
  return {
    id: `payroll_tool_${index}`,
    label: tab.label,
    description: meta.description || "Advanced payroll tool",
    path: tab.to,
    icon: meta.icon || Cog,
    roles: ["admin", "hr"],
  };
});

/**
 * Unified settings navigation — Option B category tree.
 * Setup workspace order:
 * 1 Organisation Profile → 2 Attendance → 3 Leave Settings →
 * 4 Payroll & Salary Components → 5 Statutory Setup → 6 Employee Configuration
 * (then Security / General).
 */
export const SETTINGS_CATEGORIES = [
{
    id: "organization",
    label: "Organisation Profile",
    description: "Company profile, financial year, and organization structure",
    accent: "indigo",
    items: [
      {
        id: "org_profile",
        label: "Company Profile",
        description: "Legal name, PAN, TAN, GST, address, and state registration",
        path: "/settings/organization/company-profile",
        icon: Building2,
        highlight: true,
        statusKey: "org_profile",
        roles: ["admin", "hr"],
      },
      {
        id: "financial_year",
        label: "Financial Year",
        description: "Define and activate accounting / payroll years",
        path: "/settings/organization/financial-year",
        icon: CalendarRange,
        highlight: true,
        roles: ["admin", "hr"],
      },
      {
        id: "org_structure",
        label: "Organization Structure",
        description: "Branches, departments, and designations in one place",
        path: "/settings/organization/structure",
        icon: Network,
        highlight: true,
        statusKey: "org_structure",
        roles: ["admin", "hr"],
        badges: [
          { label: "Branches", icon: MapPin },
          { label: "Departments", icon: Layers },
          { label: "Designations", icon: Users },
        ],
      },
    ],
  },
{
    id: "attendance_setup",
    label: "Attendance",
    description: "Attendance methods, shifts, roster planner, rules, weekly offs, and holiday calendar",
    accent: "emerald",
    items: [
      {
        id: "attendance_config",
        label: "Attendance Method",
        description: "Capture method — Default Present, Manual, Excel Upload, or Biometric",
        path: "/settings/attendance-leave-setup?tab=method",
        icon: CheckCircle2,
        highlight: true,
        statusKey: "attendance_config",
        roles: ["admin"],
        badges: [
          { label: "Default Present", icon: CheckCircle2 },
          { label: "Manual Entry", icon: PenLine },
          { label: "Excel Upload", icon: Upload },
          { label: "Biometric", icon: Fingerprint },
        ],
      },
      {
        id: "shift_management",
        label: "Shift Management",
        description: "Define shifts, timings, grace periods, and night-shift rules",
        path: "/settings/attendance-leave-setup?tab=shifts",
        icon: Clock,
        roles: ["admin", "hr"],
      },
      {
        id: "roster_planner",
        label: "Roster Planner",
        description: "Guide and monthly shift roster — separate from the holiday calendar",
        path: "/attendance/roster",
        icon: LayoutGrid,
        highlight: true,
        roles: ["admin", "hr"],
      },
      {
        id: "attendance_rules",
        label: "Attendance Rules",
        description: "Full-day, half-day, late, early exit, and overtime policies",
        path: "/settings/attendance-leave-setup?tab=rules",
        icon: FileText,
        roles: ["admin"],
      },
      {
        id: "weekly_off",
        label: "Weekly Off & Monthly Off",
        description: "Days off every week (e.g. Sunday) and monthly offs (e.g. 2nd Saturday) on one calendar",
        path: "/settings/attendance-leave-setup?tab=weekly-off",
        icon: CalendarOff,
        highlight: true,
        roles: ["admin", "hr"],
      },
      {
        id: "holiday_calendar",
        label: "Holiday Calendar",
        description: "Organisation holidays only — not shift rosters; used for attendance & payroll",
        path: "/holiday-calendar",
        icon: CalendarDays,
        statusKey: "holiday_calendar",
        roles: ["admin", "hr"],
      },
      {
        id: "comp_off",
        label: "Comp Off",
        description: "Earn compensatory off for working an eligible holiday or weekly off",
        path: "/settings/attendance/comp-off",
        icon: Gift,
        highlight: true,
        roles: ["admin", "hr"],
      },
      {
        id: "att_config_summary",
        label: "Configuration Summary",
        description: "View your complete attendance configuration at a glance",
        path: "/settings/attendance-leave-setup?tab=summary",
        icon: Cog,
        roles: ["admin", "hr"],
      },
    ],
  },
{
    id: "leave_setup",
    label: "Leave Settings",
    description: "Leave types, accrual, carry-forward, and approval workflow",
    accent: "violet",
    items: [
      {
        id: "leave_cycle",
        label: "Leave Cycle",
        description: "Define the organization's leave year and balance reset period",
        path: "/settings/leave-setup?tab=leave-cycle",
        icon: CalendarRange,
        highlight: true,
        roles: ["admin", "hr"],
      },
      {
        id: "leave_types",
        label: "Leave Types",
        description: "Configure CL, SL, EL and other leave categories for your organisation",
        path: "/settings/leave-setup?tab=leave-types",
        icon: ClipboardList,
        highlight: true,
        roles: ["admin", "hr"],
      },
      {
        id: "leave_policies",
        label: "Leave Policies",
        description: "Leave entitlement, carry-forward, and accrual rules for each leave type",
        path: "/settings/leave-setup?tab=leave-policies",
        icon: FileText,
        roles: ["admin", "hr"],
      },
      {
        id: "leave_approval_workflow",
        label: "Leave Approval Workflow",
        description: "Configure multi-level approval workflows for leave requests",
        path: "/settings/leave-setup?tab=approval-workflow",
        icon: GitBranch,
        roles: ["admin", "hr"],
      },
      {
        id: "leave_encashment",
        label: "Leave Encashment",
        description: "Configure Earned Leave encashment, approvals, and year-end processing",
        path: "/settings/leave/encashment",
        icon: IndianRupee,
        highlight: true,
        roles: ["admin", "hr"],
      },
    ],
  },
{
    id: "payroll_setup",
    label: "Payroll & Salary Components",
    description: "Pay cycle, salary components, and structures",
    accent: "blue",
    items: [
      {
        id: "payroll_home",
        label: "Payroll Home",
        description: "Payroll overview and quick access to payroll operations",
        path: "/payrollhome",
        icon: Wallet,
        roles: ["admin", "hr"],
        hideInSettingsHub: true,
      },
      {
        id: "payroll_settings",
        label: "Payroll Settings",
        description: "Pay cycle, processing day, payment date, and default structure",
        path: "/salary-v2/setup",
        icon: Wallet,
        statusKey: "payroll_settings",
        roles: ["admin", "hr"],
      },
      {
        id: "payroll_calendar",
        label: "Payroll Calendar",
        description: "Working days, holidays, attendance counts, and payroll dates by month",
        path: "/salary-v2/payroll-calendar",
        icon: CalendarRange,
        roles: ["admin", "hr"],
        hideInSettingsHub: true,
      },
      {
        id: "overtime_works",
        label: "Overtime Works",
        description: "Enable OT, hours source, OT pay calculation, eligibility, and OT approval",
        path: "/salary-v2/overtime",
        icon: Clock,
        highlight: true,
        roles: ["admin", "hr"],
      },
      {
        id: "extra_work_ot",
        label: "Premium Works",
        description: "Sunday work, holiday work, premium multipliers, and Comp-Off",
        path: "/salary-v2/extra-work",
        icon: Zap,
        highlight: true,
        roles: ["admin", "hr"],
      },
      {
        id: "salary_components",
        label: "Salary Components",
        description: "Earnings, deductions, and reimbursements for payroll runs",
        path: "/salary-v2/components",
        icon: Layers,
        statusKey: "salary_components",
        roles: ["admin", "hr"],
      },
      {
        id: "formula_builder",
        label: "Derived Variables / Formula Builder",
        description: "Build salary formulas, dependencies, and derived variables",
        path: "/salary-v2/formula-builder",
        icon: FunctionSquare,
        highlight: true,
        roles: ["admin", "hr"],
        hideInSettingsHub: true,
      },
      {
        id: "salary_structures",
        label: "Salary Structures",
        description: "Reusable salary structures assigned to employees",
        path: "/salary-v2/structures",
        icon: Briefcase,
        roles: ["admin", "hr"],
      },
      {
        id: "pay_groups",
        label: "Pay Groups",
        description: "Group employees by payroll cycle for salary processing",
        path: "/salary-v2/pay-groups",
        icon: Users,
        roles: ["admin", "hr"],
        hideInSettingsHub: true,
      },
    ],
  },
{
    id: "payroll_tools",
    label: "Payroll Tools (More)",
    description: "Formula builder extras, simulator, audit, and other advanced salary pages",
    accent: "cyan",
    items: PAYROLL_ADVANCED_SETTINGS_ITEMS,
  },
{
    id: "statutory_setup",
    label: "Statutory Setup",
    description: "PF, ESI, professional tax, LWF, and TDS configuration",
    accent: "amber",
    items: [
      {
        id: "pf_settings",
        label: "PF",
        description: "EPF establishment code, contribution mode, and wage limits",
        path: "/statutory/pf",
        icon: Shield,
        group: "statutory",
        roles: ["admin", "hr"],
      },
      {
        id: "esi_settings",
        label: "ESI",
        description: "ESIC registration, wage ceiling, and employer contributions",
        path: "/statutory/esi",
        icon: HeartHandshake,
        group: "statutory",
        roles: ["admin", "hr"],
      },
      {
        id: "professional_tax",
        label: "Professional Tax",
        description: "State-wise PT slabs and registration numbers",
        path: "/statutory/pt",
        icon: FileText,
        group: "statutory",
        roles: ["admin", "hr"],
      },
      {
        id: "lwf_settings",
        label: "Labour Welfare Fund",
        description: "Optional — not required for Andhra Pradesh and Telangana",
        path: "/statutory/lwf",
        icon: HeartHandshake,
        optional: true,
        group: "statutory",
        roles: ["admin", "hr"],
      },
      {
        id: "tds_settings",
        label: "TDS",
        description: "Tax deduction at source — configuration, employee tax & compliance reports",
        path: "/settings/tds",
        icon: Receipt,
        group: "statutory",
        roles: ["admin", "hr"],
      },
    ],
  },
{
    id: "allowances_benefits",
    label: "Allowances & Benefits",
    description: "Fuel reimbursement and salary advances managed in one place",
    accent: "sky",
    items: [
      {
        id: "fuel_reimbursement",
        label: "Fuel Reimbursement",
        description: "Eligibility, petrol bills, approvals, and reimbursement history",
        path: "/settings/allowances-benefits/fuel-reimbursement",
        icon: Fuel,
        highlight: true,
        roles: ["admin", "hr", "finance"],
      },
      {
        id: "advance_taken",
        label: "Advance Taken",
        description: "Salary advances, approvals, and payroll recovery history",
        path: "/settings/allowances-benefits/advance-taken",
        icon: IndianRupee,
        highlight: true,
        roles: ["admin", "hr", "finance"],
      },
      {
        id: "allowances_hub",
        label: "Allowances & Benefits",
        description: "Central hub for fuel reimbursement and advance taken",
        path: "/settings/allowances-benefits",
        icon: WalletCards,
        highlight: true,
        roles: ["admin", "hr", "finance"],
        hideInSettingsHub: true,
      },
    ],
  },
{
    id: "employee_setup",
    label: "Employee Configuration",
    description: "Configure employee types and employee numbering from one centralized location",
    accent: "teal",
    items: [
      {
        id: "employee_configuration",
        label: "Employee Configuration",
        description: "Define employment types and how employee numbers are generated",
        path: "/settings/employee-configuration",
        icon: Users,
        highlight: true,
        roles: ["admin", "hr"],
      },
    ],
  },
{
    id: "security",
    label: "Security",
    description: "Users, roles, and module-level permissions",
    accent: "purple",
    items: [
      {
        id: "users",
        label: "Users",
        description: "Invite users and manage account status",
        path: "/settings/security/users-roles?tab=users",
        icon: UserCheck,
        highlight: true,
        roles: ["admin"],
      },
      {
        id: "roles",
        label: "Roles",
        description: "Define roles such as Admin, HR, Manager, and Employee",
        path: "/settings/security/users-roles?tab=roles",
        icon: UserCog,
        roles: ["admin"],
      },
      {
        id: "permissions",
        label: "Permissions",
        description: "Module-level access control templates",
        path: "/settings/security/users-roles?tab=templates",
        icon: Lock,
        roles: ["admin"],
      },
    ],
  },
{
    id: "other",
    label: "General",
    description: "Preferences, subscription, and platform extras",
    accent: "slate",
    items: [
      {
        id: "general_settings",
        label: "General Settings",
        description: "Timezone, currency, date format, and notifications",
        path: "/settings?tab=preferences",
        icon: Cog,
        inlineTab: "preferences",
        roles: ["admin", "hr", "employee"],
      },
      {
        id: "subscription_billing",
        label: "Subscription & Plans",
        description: "Current plan, usage, Razorpay billing, upgrades, and invoices",
        path: "/settings/subscription",
        icon: CreditCard,
        highlight: true,
        roles: ["admin"],
      },
    ],
  },
]

/** Items shown on Settings home / hub cards (excludes hideInSettingsHub). */
export function filterSettingsHubItems(items = []) {
  return items.filter((item) => !item.hideInSettingsHub);
}

/** Flat list for search */
export function flattenSettingsItems(categories = SETTINGS_CATEGORIES) {
  return categories.flatMap((cat) =>
    filterSettingsHubItems(cat.items).map((item) => ({
      ...item,
      categoryId: cat.id,
      categoryLabel: cat.label,
    }))
  );
}

/** Legacy hub groups — kept for backward compatibility */
export const SETTINGS_HUB_GROUPS = SETTINGS_CATEGORIES.map((cat) => ({
  title: cat.label,
  links: filterSettingsHubItems(cat.items).map((item) => ({
    label: item.label,
    desc: item.description,
    path: item.path,
  })),
}));

/** Reserved for upcoming modules (not linked until built) */
export const SETTINGS_PENDING_MODULES = [
  "Email Notifications",
  "In-App Notifications",
  "Reminder Settings",
  "Attendance Integration",
  "Email Configuration",
];
